import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <Link to="/login" className="p-2 hover:bg-slate-100 rounded-full transition-colors" data-testid="privacy-back-btn">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </Link>
          <Shield className="w-6 h-6 text-blue-600" />
          <h1 className="text-lg font-bold text-slate-900 font-fredoka">Privacy Policy</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8" data-testid="privacy-policy-content">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8 space-y-6 text-slate-700 leading-relaxed text-sm sm:text-base">

          <p className="text-slate-500 text-xs">Last updated: April 3, 2026</p>

          <section>
            <h2 className="text-lg font-bold text-slate-900 font-fredoka mb-2">1. Introduction</h2>
            <p>
              BrainGym Jr ("we", "our", "us") is an educational application designed for children aged 6–18.
              We are committed to protecting the privacy of our users, especially children under 13, in compliance
              with the Children's Online Privacy Protection Act (COPPA) and applicable data protection laws.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 font-fredoka mb-2">2. Information We Collect</h2>
            <p className="mb-2">We collect the following information:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Child's Information:</strong> Display name (nickname), age, and quiz/game performance data.</li>
              <li><strong>Parent/Guardian Information:</strong> First name, last initial, and email address (collected at signup for parental verification and communication).</li>
              <li><strong>Payment Information:</strong> Processed securely through Stripe. We do not store credit card numbers.</li>
              <li><strong>Device Information:</strong> A device fingerprint is collected solely to prevent trial abuse. It is not used for tracking or advertising.</li>
              <li><strong>Usage Data:</strong> Quiz scores, streaks, coins earned, and learning progress to personalize the educational experience.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 font-fredoka mb-2">3. How We Use Information</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>To provide age-appropriate educational content and quizzes.</li>
              <li>To track learning progress and award in-app rewards.</li>
              <li>To process subscription payments and send receipts to parents.</li>
              <li>To prevent abuse of the free trial system.</li>
              <li>To improve our educational content and app experience.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 font-fredoka mb-2">4. Data Sharing</h2>
            <p>
              We do <strong>not</strong> sell, rent, or share personal information with third parties for marketing purposes.
              We share data only with:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Stripe:</strong> For secure payment processing.</li>
              <li><strong>OpenAI:</strong> To generate educational quiz content. No personally identifiable child information is sent to AI services.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 font-fredoka mb-2">5. Advertising</h2>
            <p>
              BrainGym Jr does <strong>not</strong> display advertisements. We do not use behavioral advertising
              or interest-based advertising of any kind, especially for users under 13.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 font-fredoka mb-2">6. COPPA Compliance</h2>
            <p>We comply with COPPA by:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Collecting parental contact information (email) before creating child accounts.</li>
              <li>Not collecting more information than necessary for the educational service.</li>
              <li>Not using behavioral tracking or targeted advertising for children under 13.</li>
              <li>Providing parents the ability to review, delete, or refuse further collection of their child's information.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 font-fredoka mb-2">7. Parental Rights</h2>
            <p>Parents and guardians have the right to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Review their child's personal information.</li>
              <li>Request deletion of their child's account and all associated data.</li>
              <li>Refuse further collection of their child's information.</li>
              <li>Contact us at <strong>write@braingymjr.ca</strong> for any data-related requests.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 font-fredoka mb-2">8. Account Deletion</h2>
            <p>
              Users or their parents can delete their account at any time through the in-app Account Settings page.
              Upon deletion, all personal data including quiz history, coins, pets, and progress will be permanently removed.
              Active subscriptions should be cancelled before account deletion.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 font-fredoka mb-2">9. Data Security</h2>
            <p>
              We use industry-standard security measures including encrypted connections (HTTPS),
              secure password hashing (bcrypt), and secure payment processing through Stripe
              to protect all user data.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 font-fredoka mb-2">10. Data Retention</h2>
            <p>
              We retain user data for as long as the account is active. Upon account deletion,
              all personal data is permanently removed within 30 days. Payment transaction records
              may be retained for legal and accounting purposes as required by law.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 font-fredoka mb-2">11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify parents via email
              of any material changes. Continued use of the app after changes constitutes acceptance
              of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 font-fredoka mb-2">12. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy or wish to exercise your parental rights, please contact us at:
            </p>
            <p className="mt-2 font-semibold">
              BrainGym Jr<br />
              Email: write@braingymjr.ca
            </p>
          </section>

        </div>
      </main>
    </div>
  );
}
